import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EXCLUSION_REASONS, exclusionLabel } from '../utils/pnlExclusions';
import {
  STATUS,
  STATUS_META,
  suggestPnlCategory,
  detectSuspiciousAssignment,
} from '../utils/pnlAssignments';

const BROWN = '#5A4A42';
const DARK_BROWN = '#3E2723';
const CREAM = '#FAF6F2';
const LIGHT_BORDER = '#E8DDD0';
const MUTED = '#8B7B73';
const FONT = "'Montserrat', sans-serif";

const ADD_VALUE = '__add';
const EXCLUDE_VALUE = '__exclude';

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META[STATUS.UNASSIGNED];
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2,
        padding: '2px 8px', borderRadius: 12,
        color: meta.color, background: meta.bg, border: `1px solid ${meta.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true">{meta.icon}</span>
      {meta.label}
    </span>
  );
}

/**
 * Row-based "Assign inventory categories to P&L categories" table.
 *
 * This replaces the old connector-line diagram with one row per inventory
 * category. Each row offers a searchable selector to choose the P&L reporting
 * category, add a new P&L category inline, or exclude the category from P&L
 * reporting. Suggested, confirmed, review, excluded and unassigned states are
 * shown with text + icon (never color alone). Renders as a table on desktop and
 * stacked cards on narrow screens.
 *
 * The component is presentational: persistence is delegated to the parent via
 * onAssignmentChange / onAddPnlCategory, so the existing pnl_category_mappings /
 * clover_category_mappings writes and IDs are preserved.
 */
export default function InventoryCategoryAssignmentTable({
  pnlCats = [],
  rows = [],
  onAssignmentChange,
  onAddPnlCategory,
  onSave,
  onCancel,
  saveLabel = 'Save Category Assignments',
  busy = false,
  error = '',
  title = 'Assign Inventory Categories to P&L Categories',
  sourceSystems = [],
  showIntro = true,
  footerNote = null,
  // Copy that differs between the inventory-setup and POS-import contexts.
  unitLabel = { singular: 'inventory category', plural: 'inventory categories' },
  unassignedConsequence = 'Items in unassigned categories will not be included in category-level P&L reporting.',
  excludeOptionLabel = 'Exclude from P&L reporting',
  excludedStatusText = 'Excluded from P&L reporting',
  showExclusionReasons = true,
}) {
  const [narrow, setNarrow] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [localError, setLocalError] = useState('');
  const [dismissed, setDismissed] = useState(() => new Set()); // suggestions the user chose to leave unassigned
  const [acknowledgedReviews, setAcknowledgedReviews] = useState(() => new Set()); // review warnings the user explicitly confirmed
  const [addingForKey, setAddingForKey] = useState(null);      // row.key | '__global' | null
  const [addName, setAddName] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);

  const containerRef = useRef(null);
  const addInputRef = useRef(null);
  const lastFocusRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const el = containerRef.current;
      if (el) setNarrow(el.offsetWidth < 620);
    };
    check();
    const obs = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    if (obs && containerRef.current) obs.observe(containerRef.current);
    return () => { if (obs) obs.disconnect(); };
  }, []);

  useEffect(() => {
    if (addingForKey && addInputRef.current) addInputRef.current.focus();
  }, [addingForKey]);

  const pnlById = useMemo(() => {
    const m = new Map();
    pnlCats.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [pnlCats]);

  // Decorate each row with its suggestion, resolved P&L name, and status.
  const decorated = useMemo(() => rows.map((row) => {
    const assignedName = row.pnlId != null ? (pnlById.get(String(row.pnlId))?.name || '') : '';
    const suggestion = (row.pnlId == null && !row.excluded && !dismissed.has(row.key))
      ? suggestPnlCategory(row.name, pnlCats)
      : null;
    const acknowledged = acknowledgedReviews.has(row.key);
    let status;
    if (row.excluded) status = STATUS.EXCLUDED;
    else if (row.pnlId != null) {
      const suspicious = detectSuspiciousAssignment(row.name, assignedName, pnlCats);
      status = (suspicious && !acknowledged) ? STATUS.REVIEW : STATUS.CONFIRMED;
    } else if (suggestion) {
      status = detectSuspiciousAssignment(row.name, suggestion.pnlName, pnlCats) ? STATUS.REVIEW : STATUS.SUGGESTED;
    } else status = STATUS.UNASSIGNED;

    const review = (status === STATUS.REVIEW)
      ? detectSuspiciousAssignment(row.name, row.pnlId != null ? assignedName : suggestion?.pnlName, pnlCats)
      : null;

    return { row, assignedName, suggestion, status, review };
  }), [rows, pnlCats, pnlById, dismissed, acknowledgedReviews]);

  const counts = useMemo(() => {
    const c = { total: decorated.length, assigned: 0, confirmed: 0, suggested: 0, review: 0, excluded: 0, unassigned: 0 };
    decorated.forEach(({ row, status }) => {
      if (row.pnlId != null) c.assigned += 1;
      if (status === STATUS.CONFIRMED) c.confirmed += 1;
      else if (status === STATUS.SUGGESTED) c.suggested += 1;
      else if (status === STATUS.REVIEW) c.review += 1;
      else if (status === STATUS.EXCLUDED) c.excluded += 1;
      else if (status === STATUS.UNASSIGNED) c.unassigned += 1;
    });
    c.resolved = c.assigned + c.excluded;
    return c;
  }, [decorated]);

  const unassignedRows = decorated.filter(({ row }) => row.pnlId == null && !row.excluded);
  const pendingSuggestions = decorated.filter(({ row, suggestion }) => row.pnlId == null && !row.excluded && suggestion);

  const runChange = async (row, change) => {
    if (!onAssignmentChange) return;
    setSavingKey(row.key);
    setLocalError('');
    try {
      await onAssignmentChange(row, change);
      // Clearing the dismissed flag keeps the in-memory state honest after a real change.
      if (change.type !== 'unassign') {
        setDismissed((prev) => { if (!prev.has(row.key)) return prev; const n = new Set(prev); n.delete(row.key); return n; });
      }
    } catch (e) {
      setLocalError(e?.message || 'Could not save that change.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleSelectChange = (row, decoratedRow, rawValue) => {
    // A manual selection change re-opens any review warning for this row.
    setAcknowledgedReviews((prev) => { if (!prev.has(row.key)) return prev; const n = new Set(prev); n.delete(row.key); return n; });
    if (rawValue === ADD_VALUE) {
      lastFocusRef.current = document.activeElement;
      setAddingForKey(row.key);
      setAddName('');
      return;
    }
    if (rawValue === EXCLUDE_VALUE) {
      runChange(row, { type: 'exclude', reason: row.exclusionReason || 'excluded' });
      return;
    }
    if (rawValue === '') {
      // Leaving unassigned. If there was a suggestion in view, remember the
      // user's choice so it isn't re-proposed this session.
      if (decoratedRow.suggestion) setDismissed((prev) => new Set(prev).add(row.key));
      if (row.pnlId != null || row.excluded) runChange(row, { type: 'unassign' });
      return;
    }
    runChange(row, { type: 'assign', pnlId: Number(rawValue) });
  };

  const confirmSuggestion = (row, suggestion) => {
    runChange(row, { type: 'assign', pnlId: suggestion.pnlId, fromSuggestion: true });
  };

  // Confirm a row flagged for review: assign its suggested P&L category (if it
  // isn't assigned yet) and acknowledge the warning so it settles as confirmed.
  const confirmReview = (row, decoratedRow) => {
    setAcknowledgedReviews((prev) => new Set(prev).add(row.key));
    if (row.pnlId == null && decoratedRow.suggestion) {
      runChange(row, { type: 'assign', pnlId: decoratedRow.suggestion.pnlId, fromSuggestion: true });
    }
  };

  const confirmAllSuggestions = async () => {
    // Acknowledge any suspicious suggestions up front so they settle as confirmed
    // (rather than flipping back to "review") once the assignment is saved.
    const reviewKeys = pendingSuggestions.filter(({ status }) => status === STATUS.REVIEW).map(({ row }) => row.key);
    if (reviewKeys.length) {
      setAcknowledgedReviews((prev) => { const n = new Set(prev); reviewKeys.forEach((k) => n.add(k)); return n; });
    }
    for (const { row, suggestion } of pendingSuggestions) {
      // Sequential to keep each Supabase write ordered and surface the first error.
      // eslint-disable-next-line no-await-in-loop
      await runChange(row, { type: 'assign', pnlId: suggestion.pnlId, fromSuggestion: true });
    }
  };

  const submitAddCategory = async () => {
    const name = addName.trim();
    if (!name || !onAddPnlCategory) return;
    setAddBusy(true);
    setLocalError('');
    try {
      const cat = await onAddPnlCategory(name);
      const forKey = addingForKey;
      setAddingForKey(null);
      setAddName('');
      if (cat && forKey && forKey !== '__global') {
        const row = rows.find((r) => r.key === forKey);
        if (row) await runChange(row, { type: 'assign', pnlId: cat.id });
      }
      // Return focus where the add flow began (accessibility).
      if (lastFocusRef.current && typeof lastFocusRef.current.focus === 'function') {
        lastFocusRef.current.focus();
      }
    } catch (e) {
      setLocalError(e?.message || 'Could not add that P&L category.');
    } finally {
      setAddBusy(false);
    }
  };

  const cancelAdd = () => {
    setAddingForKey(null);
    setAddName('');
    if (lastFocusRef.current && typeof lastFocusRef.current.focus === 'function') lastFocusRef.current.focus();
  };

  const selectValueFor = (row, decoratedRow) => {
    if (row.excluded) return EXCLUDE_VALUE;
    if (row.pnlId != null) return String(row.pnlId);
    if (decoratedRow.suggestion) return String(decoratedRow.suggestion.pnlId);
    return '';
  };

  // ── Sub-renderers ────────────────────────────────────────────────────────
  const selectStyle = {
    padding: '6px 9px', borderRadius: 8, border: `1px solid ${LIGHT_BORDER}`,
    fontSize: '0.82rem', fontFamily: FONT, background: '#fff', width: '100%',
    maxWidth: narrow ? '100%' : 260, boxSizing: 'border-box', color: DARK_BROWN,
  };

  const renderSelect = (row, decoratedRow) => (
    <select
      value={selectValueFor(row, decoratedRow)}
      disabled={busy || savingKey === row.key}
      onChange={(e) => handleSelectChange(row, decoratedRow, e.target.value)}
      aria-label={`P&L reporting category for ${row.name}${row.source ? ` (${row.source})` : ''}`}
      style={{
        ...selectStyle,
        borderColor: decoratedRow.status === STATUS.REVIEW ? '#E2B23A'
          : decoratedRow.status === STATUS.SUGGESTED ? '#E0C089' : LIGHT_BORDER,
        background: decoratedRow.status === STATUS.SUGGESTED ? '#FFFCF6' : '#fff',
      }}
    >
      <option value="">Leave unassigned</option>
      {pnlCats.length > 0 && (
        <optgroup label="Report under">
          {pnlCats.map((c) => (
            <option key={c.id} value={String(c.id)}>{c.name}</option>
          ))}
        </optgroup>
      )}
      <optgroup label="Other">
        <option value={ADD_VALUE}>＋ Add a new P&amp;L category…</option>
        <option value={EXCLUDE_VALUE}>{excludeOptionLabel}</option>
      </optgroup>
    </select>
  );

  const renderRowExtras = (row, decoratedRow) => (
    <>
      {/* Inline "add a new P&L category" for this row */}
      {addingForKey === row.key && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            ref={addInputRef}
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitAddCategory(); if (e.key === 'Escape') cancelAdd(); }}
            placeholder="New P&L category name…"
            aria-label="New P&L category name"
            style={{ flex: '1 1 160px', padding: '5px 9px', borderRadius: 7, border: `1px solid ${LIGHT_BORDER}`, fontSize: '0.8rem', fontFamily: FONT, outline: 'none' }}
          />
          <button type="button" onClick={submitAddCategory} disabled={addBusy || !addName.trim()}
            style={{ padding: '5px 12px', borderRadius: 7, background: '#2c7a4b', color: '#fff', border: 'none', cursor: addBusy || !addName.trim() ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 700, opacity: addBusy || !addName.trim() ? 0.6 : 1, fontFamily: FONT }}>
            Add
          </button>
          <button type="button" onClick={cancelAdd}
            style={{ padding: '5px 12px', borderRadius: 7, background: 'none', color: MUTED, border: `1px solid ${LIGHT_BORDER}`, cursor: 'pointer', fontSize: '0.78rem', fontFamily: FONT }}>
            Cancel
          </button>
        </div>
      )}

      {/* Suggested → offer an explicit confirm (also confirmed by saving).
          Review rows confirm via the yellow "Confirm?" status button instead. */}
      {decoratedRow.status === STATUS.SUGGESTED && decoratedRow.suggestion && row.pnlId == null && !row.excluded && (
        <div style={{ marginTop: 6, fontSize: '0.74rem', color: '#7B5E45', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Suggested: <strong>{decoratedRow.suggestion.pnlName}</strong></span>
          <button type="button" onClick={() => confirmSuggestion(row, decoratedRow.suggestion)} disabled={busy || savingKey === row.key}
            style={{ padding: '2px 10px', borderRadius: 12, background: '#2c7a4b', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, fontFamily: FONT }}>
            ✓ Confirm
          </button>
        </div>
      )}

      {/* Excluded → optional reason refinement (auditability) */}
      {row.excluded && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.73rem', color: MUTED }}>
            {excludedStatusText}{showExclusionReasons ? ` · ${exclusionLabel(row.exclusionReason)}` : ''}
          </span>
          {showExclusionReasons && (
            <select
              value={row.exclusionReason || 'excluded'}
              disabled={busy || savingKey === row.key}
              onChange={(e) => runChange(row, { type: 'exclude', reason: e.target.value })}
              aria-label={`Exclusion reason for ${row.name}`}
              style={{ padding: '3px 7px', borderRadius: 6, border: `1px solid ${LIGHT_BORDER}`, fontSize: '0.73rem', fontFamily: FONT, background: '#fff' }}
            >
              {EXCLUSION_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          )}
        </div>
      )}
    </>
  );

  const renderCategoryCell = (row, decoratedRow) => {
    const hasExamples = Array.isArray(row.examples) && row.examples.length > 0;
    return (
      <div>
        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: DARK_BROWN }}>{row.name}</div>
        <div style={{ fontSize: '0.72rem', color: MUTED, marginTop: 1 }}>
          {row.source || 'Inventory'}
          {row.itemCount != null ? ` · ${row.itemCount} item${row.itemCount === 1 ? '' : 's'}` : ''}
          {row.detail ? ` · ${row.detail}` : ''}
        </div>
        {hasExamples && (
          <button type="button"
            onClick={() => setExpandedKey(expandedKey === row.key ? null : row.key)}
            aria-expanded={expandedKey === row.key}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: BROWN, fontSize: '0.7rem', textDecoration: 'underline', padding: '2px 0 0', fontFamily: FONT }}>
            {expandedKey === row.key ? 'Hide items' : 'Show items'}
          </button>
        )}
        {hasExamples && expandedKey === row.key && (
          <div style={{ fontSize: '0.72rem', color: MUTED, marginTop: 2 }}>{row.examples.join(', ')}</div>
        )}
      </div>
    );
  };

  // Status cell: a review becomes a yellow "Confirm?" button that assigns the
  // (single) P&L category to this inventory category and clears the warning.
  const renderStatus = (decoratedRow) => {
    if (decoratedRow.status === STATUS.REVIEW) {
      const { row } = decoratedRow;
      const target = row.pnlId != null ? decoratedRow.assignedName : decoratedRow.suggestion?.pnlName;
      return (
        <button
          type="button"
          onClick={() => confirmReview(row, decoratedRow)}
          disabled={busy || savingKey === row.key}
          title={decoratedRow.review?.message || ''}
          aria-label={target ? `Confirm that ${row.name} reports under ${target}` : `Confirm assignment for ${row.name}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2,
            padding: '3px 11px', borderRadius: 12,
            color: '#8a6d1b', background: '#FFF8E1', border: '1px solid #F0D78A',
            cursor: busy || savingKey === row.key ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap', fontFamily: FONT,
          }}
        >
          <span aria-hidden="true">⚠</span> Confirm?
        </button>
      );
    }
    return <StatusChip status={decoratedRow.status} />;
  };

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{ fontFamily: FONT }}>
      {showIntro && (
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: DARK_BROWN, fontWeight: 800, fontSize: '1.05rem' }}>{title}</h3>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.82rem', color: BROWN, lineHeight: 1.5 }}>
            Inventory categories describe how items are organized in your inventory system. P&amp;L
            categories describe how those costs are grouped on your profit and loss report. Assign each
            inventory category to the P&amp;L category where its cost should appear.
          </p>
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: MUTED, lineHeight: 1.5 }}>
            Your existing P&amp;L categories appear in each selector below. Add a new one anytime if
            your business or reporting structure has changed.
          </p>
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: MUTED, lineHeight: 1.5 }}>
            Example: Produce, Meat, and Dry Goods may all report under Food.
            {sourceSystems.length > 1 && (
              <> Inventory categories shown below come from {sourceSystems.join(' and ')}.</>
            )}
          </p>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: DARK_BROWN, fontWeight: 600 }}>
            Choose where each inventory category should report on your P&amp;L. Multiple inventory
            categories can report to the same P&amp;L category.
          </p>
        </div>
      )}

      {(error || localError) && (
        <div role="alert" style={{ background: '#FDECEA', border: '1px solid #F5C6CB', borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: '0.7rem', fontSize: '0.78rem', color: '#721C24' }}>
          {error || localError}
        </div>
      )}

      {/* Progress + bulk actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.86rem', fontWeight: 700, color: DARK_BROWN }}>
            {counts.resolved} of {counts.total} {unitLabel.plural} assigned
            {counts.review > 0 && <span style={{ color: '#8a6d1b' }}> · {counts.review} need{counts.review === 1 ? 's' : ''} review</span>}
          </div>
          <div style={{ fontSize: '0.72rem', color: MUTED, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {counts.confirmed > 0 && <span>{counts.confirmed} confirmed</span>}
            {counts.suggested > 0 && <span>{counts.suggested} suggested</span>}
            {counts.review > 0 && <span>{counts.review} need review</span>}
            {counts.excluded > 0 && <span>{counts.excluded} excluded</span>}
            {counts.unassigned > 0 && <span>{counts.unassigned} unassigned</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {pendingSuggestions.length > 0 && (
            <button type="button" onClick={confirmAllSuggestions} disabled={busy}
              style={{ padding: '6px 12px', borderRadius: 8, background: '#F0FAF2', color: '#1a5c34', border: '1px solid #A9DCBC', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700, fontFamily: FONT }}>
              ✓ Confirm {pendingSuggestions.length} suggestion{pendingSuggestions.length === 1 ? '' : 's'}
            </button>
          )}
          <button type="button"
            onClick={() => { lastFocusRef.current = document.activeElement; setAddingForKey('__global'); setAddName(''); }}
            style={{ padding: '6px 12px', borderRadius: 8, background: CREAM, color: '#7B5E45', border: '1px dashed #C8B8A8', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700, fontFamily: FONT }}>
            ＋ Add P&amp;L category
          </button>
        </div>
      </div>

      {addingForKey === '__global' && (
        <div style={{ marginBottom: '0.8rem', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', background: '#FFFDF9', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 10, padding: '0.6rem 0.75rem' }}>
          <input
            ref={addInputRef}
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitAddCategory(); if (e.key === 'Escape') cancelAdd(); }}
            placeholder="New P&L category name…"
            aria-label="New P&L category name"
            style={{ flex: '1 1 200px', padding: '6px 10px', borderRadius: 7, border: `1px solid ${LIGHT_BORDER}`, fontSize: '0.82rem', fontFamily: FONT, outline: 'none' }}
          />
          <button type="button" onClick={submitAddCategory} disabled={addBusy || !addName.trim()}
            style={{ padding: '6px 14px', borderRadius: 7, background: '#2c7a4b', color: '#fff', border: 'none', cursor: addBusy || !addName.trim() ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 700, opacity: addBusy || !addName.trim() ? 0.6 : 1, fontFamily: FONT }}>
            Add
          </button>
          <button type="button" onClick={cancelAdd}
            style={{ padding: '6px 14px', borderRadius: 7, background: 'none', color: MUTED, border: `1px solid ${LIGHT_BORDER}`, cursor: 'pointer', fontSize: '0.8rem', fontFamily: FONT }}>
            Cancel
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: MUTED, fontSize: '0.85rem', border: `1px dashed ${LIGHT_BORDER}`, borderRadius: 10 }}>
          No {unitLabel.plural} found yet.
        </div>
      ) : narrow ? (
        // ── Mobile: stacked cards ──
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {decorated.map((d) => (
            <div key={d.row.key} style={{ border: `1px solid ${LIGHT_BORDER}`, borderRadius: 12, padding: '0.75rem 0.85rem', background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                {renderCategoryCell(d.row, d)}
                {renderStatus(d)}
              </div>
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: '0.68rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>
                  Reports to
                </label>
                {renderSelect(d.row, d)}
                {renderRowExtras(d.row, d)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // ── Desktop: table ──
        <div style={{ border: `1px solid ${LIGHT_BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#FAF3EC', textAlign: 'left' }}>
                <th style={{ padding: '9px 12px', color: DARK_BROWN, fontWeight: 700, borderBottom: `2px solid ${LIGHT_BORDER}` }}>Inventory Category</th>
                <th style={{ padding: '9px 12px', color: DARK_BROWN, fontWeight: 700, borderBottom: `2px solid ${LIGHT_BORDER}`, width: 290 }}>Reports To</th>
                <th style={{ padding: '9px 12px', color: DARK_BROWN, fontWeight: 700, borderBottom: `2px solid ${LIGHT_BORDER}`, width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {decorated.map((d, i) => (
                <tr key={d.row.key} style={{ background: i % 2 === 0 ? '#FFFDF9' : CREAM, borderBottom: `1px solid ${LIGHT_BORDER}`, verticalAlign: 'top' }}>
                  <td style={{ padding: '10px 12px' }}>{renderCategoryCell(d.row, d)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {renderSelect(d.row, d)}
                    {renderRowExtras(d.row, d)}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{renderStatus(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Validation note for remaining unassigned categories */}
      {unassignedRows.length > 0 && (
        <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: '#856404', background: '#FFF8DC', border: '1px solid #FFE082', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
          <strong>{unassignedRows.length}</strong> {unassignedRows.length === 1 ? unitLabel.singular : unitLabel.plural} still need{unassignedRows.length === 1 ? 's' : ''} a P&amp;L assignment:{' '}
          <span style={{ opacity: 0.9 }}>{unassignedRows.map(({ row }) => row.name).join(', ')}</span>
          <div style={{ marginTop: 4, color: '#7a6a14' }}>
            {unassignedConsequence}
          </div>
        </div>
      )}

      {footerNote && (
        <div style={{ marginTop: '0.75rem', fontSize: '0.76rem', color: MUTED }}>{footerNote}</div>
      )}

      {/* Accept all recommendations → assign every suggested P&L category at once */}
      {pendingSuggestions.length > 0 && (
        <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', background: '#F0FAF2', border: '1px solid #A9DCBC', borderRadius: 10, padding: '0.7rem 0.9rem' }}>
          <button type="button" onClick={confirmAllSuggestions} disabled={busy}
            style={{ background: '#2c7a4b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: busy ? 'not-allowed' : 'pointer', fontSize: '0.84rem', fontWeight: 700, fontFamily: FONT, opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            ✓ Accept all recommendations
          </button>
          <span style={{ fontSize: '0.78rem', color: '#1a5c34' }}>
            Assigns the recommended P&amp;L category to {pendingSuggestions.length} {pendingSuggestions.length === 1 ? unitLabel.singular : unitLabel.plural}.
          </span>
        </div>
      )}

      {(onSave || onCancel) && (
        <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, paddingTop: '1rem', borderTop: `1px solid ${LIGHT_BORDER}` }}>
          {onCancel ? (
            <button type="button" onClick={onCancel} disabled={busy}
              style={{ background: 'none', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: '0.84rem', color: MUTED, fontFamily: FONT }}>
              Cancel
            </button>
          ) : <span />}
          {onSave && (
            <button type="button" onClick={() => onSave({ counts, confirmAllSuggestions })} disabled={busy}
              style={{ background: '#2c7a4b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', cursor: busy ? 'not-allowed' : 'pointer', fontSize: '0.88rem', fontWeight: 700, fontFamily: FONT, opacity: busy ? 0.6 : 1 }}>
              {saveLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
